import { Controller } from '@hotwired/stimulus';

// Collects several offices from the kit's single-city field, serialised as JSON into
// #job_ad_office_locations. That shape is a contract: JobAdForm#office_locations is
// Array[Location]. Every change is announced on the document as
// `job-post-office-locations:change` carrying the offices' country names, so the page can
// react without reading the field back.
export default class extends Controller {
  static targets = ['chips', 'chipTemplate', 'field'];

  static values = { offices: Array, countryNames: Object };

  connect() {
    this.offices = [...this.officesValue];
    this.render({ notify: false });
  }

  add(event) {
    const location = event.detail?.location;
    if (!location?.city) return;

    const alreadyListed = this.offices.some(office => this.keyOf(office) === this.keyOf(location));
    if (!alreadyListed) {
      this.offices.push(location);
      this.render();
    }

    this.clearField();
  }

  remove(event) {
    const key = String(event.params.office);

    this.offices = this.offices.filter(office => this.keyOf(office) !== key);
    this.render();
  }

  clearAll() {
    this.offices = [];
    this.render();
    this.clearField();
  }

  render({ notify = true } = {}) {
    this.chipsTarget.replaceChildren(...this.offices.map(office => this.chipFor(office)));
    this.chipsTarget.hidden = this.offices.length === 0;

    if (this.hasFieldTarget) this.fieldTarget.value = JSON.stringify(this.offices);
    if (notify) this.dispatch('change', { target: document, detail: { countries: this.countryNames() } });
  }

  chipFor(office) {
    const item = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    const label = this.labelFor(office);
    const key = this.keyOf(office);

    item.dataset.office = key;
    this.inItem(item, 'label').textContent = label;

    const removeButton = this.inItem(item, 'removeButton');
    removeButton.setAttribute('aria-label', `Remove ${label}`);
    removeButton.setAttribute(`data-${this.identifier}-office-param`, key);

    return item;
  }

  inItem(item, target) {
    return item.querySelector(`[data-${this.identifier}-target="${target}"]`);
  }

  // A city name alone is not an identity: two cities of the same name in different
  // countries are two offices. The place id is not part of it either, so an office picked
  // today and the same one saved before place ids were recorded count once.
  keyOf(office) {
    return [office.city, office.country_code].filter(Boolean).join(', ');
  }

  labelFor(office) {
    return [office.city, this.countryNameFor(office)].filter(Boolean).join(', ');
  }

  countryNames() {
    return [...new Set(this.offices.map(office => this.countryNameFor(office)).filter(Boolean))];
  }

  countryNameFor(office) {
    return office.country || this.countryNamesValue[office.country_code];
  }

  clearField() {
    this.dispatch('clear-field', { target: document });
  }
}
